import { ApdmUploadButton } from '@euler/app/flows/apdm/imports/ApdmUploadButton';
import { DictationMic } from '@euler/components/voice-input/DictationMic';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { ArrowUp } from 'lucide-react-native';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { AttachmentChip } from '../components/AttachmentChip';
import { submitOnEnter } from '../components/submitOnEnter';
import { aiColors, aiElevation } from '../theme';

const kMaxAttachments = 5;
const kRing = 3;
/** The brand blue at a quarter on white. */
const kRingColor = '#C6DBF5';

/** The home prompt: what the user wants, plus the sheet it is about. */
export const AiPromptCard = memo(
  ({
    onSubmit,
  }: {
    onSubmit: (text: string, attachments: AiAttachmentInfo[]) => boolean;
  }) => {
    const styles = useStyles();
    const [text, setText] = useState('');
    const [attachments, setAttachments] = useState<AiAttachmentInfo[]>([]);
    const [focused, setFocused] = useState(false);
    const [dictating, setDictating] = useState(false);
    const mounted = useRef(true);
    useEffect(
      () => () => {
        mounted.current = false;
      },
      [],
    );
    const ready = !dictating && (!!text.trim() || attachments.length > 0);
    const submit = () => {
      if (ready && onSubmit(text, attachments)) {
        setText('');
        setAttachments([]);
      }
    };
    // Words held and let go of on 发送, or sent from the editor: they go with
    // the sheets picked; not taken, they wait in the box.
    const submitSpoken = (words: string) => {
      if (onSubmit(words, attachments)) setAttachments([]);
      else setText(previous => previous + words);
    };
    return (
      <View style={[styles.ring, focused && styles.ringFocused]}>
        <View style={[styles.card, focused && styles.cardFocused]}>
          <TextInput
            accessibilityLabel="告诉知车 AI 您想做什么"
            value={text}
            onChangeText={setText}
            onKeyPress={submitOnEnter(submit)}
            placeholder="说说您想做什么，例如：给半年没来的老客户发个保养优惠"
            placeholderTextColor={aiColors.mutedForeground}
            multiline
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={styles.input}
          />
          {attachments.length > 0 && (
            <View style={styles.attachments}>
              {attachments.map(info => (
                <AttachmentChip
                  key={info.id}
                  info={info}
                  onRemove={() =>
                    setAttachments(previous =>
                      previous.filter(item => item.id !== info.id),
                    )
                  }
                />
              ))}
            </View>
          )}
          <View style={styles.actions}>
            <ApdmUploadButton
              appearance="chip"
              disabled={attachments.length >= kMaxAttachments}
              onUploaded={async info => {
                if (!mounted.current) return;
                setAttachments(previous =>
                  previous.some(item => item.id === info.id)
                    ? previous
                    : [...previous, info].slice(0, kMaxAttachments),
                );
              }}
            />
            <View style={styles.trailing}>
              {/* On a phone: a tap dictates into the box, a hold talks. */}
              {Platform.OS === 'web' ? null : (
                <DictationMic
                  value={text}
                  onChangeText={setText}
                  onSend={submitSpoken}
                  onDictatingChange={setDictating}
                />
              )}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="发送"
                disabled={!ready}
                onPress={submit}
                style={[styles.send, !ready && styles.sendDisabled]}
              >
                <ArrowUp size={18} color={aiColors.primaryForeground} />
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: aiColors.card,
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          padding: rpx(16),
          ...aiElevation.raised,
        },
        // A ring in the brand color while typing. It is always there,
        // drawn outside the card's own box, so focusing moves nothing.
        ring: {
          margin: -kRing,
          padding: kRing,
          borderRadius: rpx(14) + kRing,
        },
        // Opaque: the card's top half sits on the blue header, where a
        // translucent blue ring, like the blue border, disappears.
        ringFocused: { backgroundColor: kRingColor },
        // The ring marks focus; the card's own border merges into it.
        cardFocused: { borderColor: kRingColor },
        input: {
          minHeight: rpx(64),
          maxHeight: rpx(160),
          fontSize: rpx(15),
          lineHeight: rpx(22),
          color: aiColors.foreground,
          textAlignVertical: 'top',
          padding: 0,
        },
        attachments: { marginTop: rpx(10), gap: rpx(8) },
        actions: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: rpx(12),
        },
        trailing: { flexDirection: 'row', alignItems: 'center', gap: rpx(8) },
        send: {
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          backgroundColor: aiColors.primary,
          alignItems: 'center',
          justifyContent: 'center',
        },
        sendDisabled: { opacity: 0.45 },
      }),
    [rpx],
  );
}
